import React, {memo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionCheck} from '../../../../../srcCommon/permissions/permissionCheck';
import {ApplicationComposerToolbarEntryPointModel} from '../../../../models/api/applicationEntryPointModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {FlexSpacer} from '../../../common/flex';
import {BaseComposerToolbar} from './baseComposerToolbar';
import {AnswersToolbarButtonProps} from './buttons/answersToolbarButton';
import {AnswersToolbarButtonContainer} from './buttons/answersToolbarButtonContainer';
import {AttachmentsToolbarButtonPublicProps} from './buttons/attachmentsToolbarButton';
import {AttachmentsToolbarButtonContainer} from './buttons/attachmentsToolbarButtonContainer';
import {DeleteToolbarButton, DeleteToolbarButtonProps} from './buttons/deleteToolbarButton';
import {DoneToolbarButton, DoneToolbarButtonProps} from './buttons/doneToolbarButton';
import {EmojiToolbarButton, EmojiToolbarButtonProps} from './buttons/emojiToolbarButton';
import {
  KnowledgeBaseToolbarButton,
  KnowledgeBaseToolbarButtonProps,
} from './buttons/knowledgeBaseToolbarButton';
import {
  SendToolbarButtonContainer,
  SendToolbarButtonContainerProps,
} from './buttons/sendToolbarButtonContainer';
import {DraftSaveIndicator, DraftSaveStatusesEnum} from './draftSaveIndicator';
import {ApplicationComposerEntrypointButtons} from './entryPoints/applicationComposerEntryPointButtons';

/*
 * Props.
 */

export interface ChatComposerToolbarProps
  extends EmojiToolbarButtonProps,
    AttachmentsToolbarButtonPublicProps,
    Omit<AnswersToolbarButtonProps, 'channelId'>,
    Omit<DeleteToolbarButtonProps, 'isDisabled' | 'isClearButton'>,
    Omit<SendToolbarButtonContainerProps, 'hasArchivePermission'>,
    DoneToolbarButtonProps,
    KnowledgeBaseToolbarButtonProps {
  channel: ChannelModel;
  message: DraftMessageModel;
  panelId: ComposerPanelId | ViewerPanelId;
  isUserVisitor?: boolean;
  canSendOnEnter: boolean;
  shouldSendOnEnter: boolean;
  updateShouldSendOnEnter: (arg: boolean) => void;
  draftSaveStatus: DraftSaveStatusesEnum;
  isDraftSaving: boolean;
  needsFormattingToolbar: boolean;
  isFormattingToolbarOpen: boolean;
  toggleIsFormattingToolbarOpen: () => void;
  timelineVariation: TimelineVariationsEnum;
  composerEntryPoints?: ReadonlyArray<ApplicationComposerToolbarEntryPointModel>;
  popoutConversationTransitionViewer: () => void;
}

/*
 * Style.
 */

const StyledEnterToSendDiv = styled.div`
  min-width: 0;
  margin: 0 10px;
  display: flex;
  align-items: center;
`;

const StyledDividerDiv = styled.div`
  margin-left: 12px;
  margin-right: 2px;
  width: 1px;
  background-color: ${(props) => props.theme.alphas.black20};
`;

/*
 * Component.
 */

export const ChatComposerToolbar = memo<ChatComposerToolbarProps>((props) => {
  const isFrontChat = props.channel.type === ChannelTypesEnum.FRONT_CHAT;
  const hasChatConversationTransition = isFrontChat && props.draftSaveStatus !== DraftSaveStatusesEnum.ERROR;

  const willOpenComposerByDefault = props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;
  const canDelete =
    !props.isUserVisitor && (!willOpenComposerByDefault || props.message.wasSaved || props.message.isSaving);

  return (
    <UserThemeConsumer>
      {(theme) => (
        <BaseComposerToolbar isTopBorderHidden={!props.isFormattingToolbarOpen}>
          {props.needsFormattingToolbar && (
            <ToolbarButton
              iconName="formatting"
              onClick={props.toggleIsFormattingToolbarOpen}
              label={<Trans id="composer-formatting">Formatting options</Trans>}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              hasHover={props.isFormattingToolbarOpen}
            />
          )}
          <EmojiToolbarButton {...props} />
          <AttachmentsToolbarButtonContainer {...props} />
          {!props.isUserVisitor && <AnswersToolbarButtonContainer {...props} channelId={props.channel.id} />}
          {!props.isUserVisitor && <KnowledgeBaseToolbarButton {...props} />}
          {props.composerEntryPoints && (
            <ApplicationComposerEntrypointButtons
              panelId={props.panelId}
              entryPoints={props.composerEntryPoints}
              draftMessageUid={props.message.uid}
            />
          )}
          {canDelete && <DeleteToolbarButton {...props} isClearButton={willOpenComposerByDefault} />}
          <FlexSpacer />
          <DraftSaveIndicator status={props.draftSaveStatus} isSaving={props.isDraftSaving} />
          {renderRightActions(props, hasChatConversationTransition)}
        </BaseComposerToolbar>
      )}
    </UserThemeConsumer>
  );
});

function renderRightActions(props: ChatComposerToolbarProps, hasChatConversationTransition: boolean) {
  if (props.isUserVisitor) {
    return <DoneToolbarButton {...props} />;
  }

  const onTransitionClick = () => {
    props.popoutConversationTransitionViewer();
  };

  return (
    <>
      {hasChatConversationTransition && (
        <>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onTransitionClick}>
            <Trans id="chatcomposer-replyviaemail">Reply via email</Trans>
          </Button>
          <StyledDividerDiv />
        </>
      )}
      {maybeRenderEnterToSend(props)}
      <PermissionCheck namespaces={[props.namespace]} permission={PermissionsEnum.CONVERSATION_ARCHIVE}>
        {(archivePermissionResult) => (
          <SendToolbarButtonContainer
            cellsContext={props.cellsContext}
            conversation={props.conversation}
            channel={props.channel}
            hasValidationErrors={props.hasValidationErrors}
            areSendShortcutsDisabled={props.areSendShortcutsDisabled}
            hasClosePermission={props.hasClosePermission}
            hasSendPermission={props.hasSendPermission}
            defaultSendAction={props.defaultSendAction}
            sendMessage={props.sendMessage}
            hasTrialExpired={props.hasTrialExpired}
            hasTeamTrialExpired={props.hasTeamTrialExpired}
            timelineVariation={props.timelineVariation}
            hasArchivePermission={archivePermissionResult.isAllowed}
          />
        )}
      </PermissionCheck>
    </>
  );
}

function maybeRenderEnterToSend(props: ChatComposerToolbarProps) {
  if (!props.canSendOnEnter) {
    return null;
  }

  return (
    <StyledEnterToSendDiv>
      <CheckBox isChecked={props.shouldSendOnEnter} onChange={props.updateShouldSendOnEnter}>
        <Trans id="composer-entertosend">&quot;Enter&quot; to send</Trans>
      </CheckBox>
    </StyledEnterToSendDiv>
  );
}
